import {
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  TooltipContent,
  TooltipTrigger,
  DrawerTrigger,
  DrawerContent,
  DropdownMenu,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
  Typography,
  DataTable,
  Separator,
  Tooltip,
  Button,
  Drawer,
  Anchor,
  Input,
  Label,
} from '@ui/components';
import { HasPermission } from '@shared/components/permission/has-permission';
import { MenuVertical, Cancel, Wallet, Info } from '@ui/components/icons';
import { PaymentCard } from '@pages/cost-centers/components';
import { PropsWithChildren, useState, FC } from 'react';
import { CheckImpersonation } from '@shared/components';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';

import { CostCenterRemoveCardDialog } from './cost-center-remove-card-dialog/cost-center-remove-card-dialog';
import { useCostCenterAccountDetails } from './use-cost-center-account-details';

export const CostCenterAccountDetails: FC<PropsWithChildren<{ costCenterId: string }>> = ({
  children,
  costCenterId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isEditCardsPending,
    editPaymentCards,
    costCenterData,
    columnsLength,
    resetCards,
    removeCard,
    companyId,
    isLoading,
    table,
    cards,
    t,
  } = useCostCenterAccountDetails({ costCenterId, onSuccess: () => setIsOpen(false) });
  const [removeCardId, setRemoveCardId] = useState<string | null>(null);

  return (
    <Drawer
      onOpenChange={(value) => {
        setIsOpen(value);
        setRemoveCardId(null);
        resetCards();
      }}
      open={isOpen}
      handleOnly
    >
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="flex justify-between">
          <DrawerTitle>{t('COST_CENTER.ACCOUNT_DETAILS.TITLE')}</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" type="button" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto p-4 -mt-4 space-y-4">
          <section className="grid grid-cols-8 gap-4 items-center">
            <Label htmlFor="accountNumber" className="col-span-2">
              <Typography variant="labelSmallBook" className="text-weak">
                {t('COST_CENTER.ACCOUNT_DETAILS.ACCOUNT_NUMBER')}
              </Typography>
            </Label>
            <div className="col-span-5">
              <Input
                value={costCenterData?.accountIban}
                name="accountNumber"
                id="accountNumber"
                inputSize="small"
                readonly
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="text-weak" />
              </TooltipTrigger>
              <TooltipContent>
                <Typography variant="labelSmallBook">
                  {t('COST_CENTER.ACCOUNT_DETAILS.ACCOUNT_NUMBER_TOOLTIP')}
                </Typography>
              </TooltipContent>
            </Tooltip>
          </section>

          <Separator />

          <section className="flex flex-col gap-5 mt-5">
            <Typography variant="h200">
              {t('COST_CENTER.ACCOUNT_DETAILS.LINKED_CARDS_TITLE')}
            </Typography>

            <div className="flex flex-col gap-4">
              {cards.map((card) => (
                // TODO: Add enum for card status
                <PaymentCard
                  subtitle={`${t('COST_CENTER.ACCOUNT_DETAILS.VALID_DATE')}: ${card.cardExpirationMonth}/${card.cardExpirationYear}`}
                  title={`${card.cardBrand || '-'} ${card.cardNumber || '-'}`}
                  icon={<img src={card.imageUrl} alt="card-brand" />}
                  disabled={card.paymentMethodId === 'INACTIVE'}
                  key={card.paymentMethodId}
                >
                  {/* <Typography variant="labelSmallBook">{card.paymentMethodId}</Typography> */}
                  <CheckImpersonation>
                    <HasPermission
                      permissionFeature="COST_CENTER_ACCOUNT_EDIT_CARDS"
                      permissionType="WRITE"
                    >
                      <DropdownMenu>
                        <Tooltip>
                          <DropdownMenuTrigger asChild>
                            <TooltipTrigger asChild>
                              {/* TODO: remove disable here when there will be more then 1 action available */}
                              <Button
                                disabled={cards.length < 2}
                                variant="ghost"
                                type="button"
                                size="small"
                              >
                                <MenuVertical />
                              </Button>
                            </TooltipTrigger>
                          </DropdownMenuTrigger>
                          <TooltipContent>
                            <Typography variant="labelSmallBook" element="span">
                              {t('COST_CENTER.ACCOUNT_DETAILS.MORE_ACTIONS')}
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setRemoveCardId(card.paymentMethodId)}
                            disabled={cards.length < 2}
                          >
                            <Typography variant="labelSmallBook">
                              {t('COST_CENTER.ACCOUNT_DETAILS.CARD_REMOVE')}
                            </Typography>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </HasPermission>
                  </CheckImpersonation>
                </PaymentCard>
              ))}
            </div>
          </section>

          <Separator />

          <section className="flex flex-col gap-4 mt-6">
            <div className="flex items-center gap-2">
              <Wallet className="size-4" />
              <Typography variant="h200">
                {t('COST_CENTER.ACCOUNT_DETAILS.TOP_UPS_TITLE')}
              </Typography>
            </div>

            <div className="overflow-x-hidden overflow-y-auto">
              <DataTable
                noResultsComponent={
                  <Typography variant="labelSmallBook">
                    {t('COST_CENTER.ACCOUNT_DETAILS.NO_TOP_UPS')}
                  </Typography>
                }
                columnsLength={columnsLength}
                isLoading={isLoading}
                table={table}
                hideHeader
              />
            </div>
            <Anchor asChild>
              <Link
                to={`/${companyId}/${Routes.FILES_REPORTS}/${Routes.REPORTS}`}
                className="block mt-2"
              >
                <Button variant="link" wide>
                  {t('COST_CENTER.ACCOUNT_DETAILS.REPORT_BUTTON')}
                </Button>
              </Link>
            </Anchor>
          </section>
        </div>

        <CostCenterRemoveCardDialog
          onRemove={() => removeCardId && removeCard(removeCardId)}
          onOpenChange={() => setRemoveCardId(null)}
          open={!!removeCardId}
        />

        <DrawerFooter>
          <HasPermission permissionFeature="COST_CENTER_ACCOUNT_EDIT_CARDS" permissionType="WRITE">
            <Button
              isLoading={isEditCardsPending || isLoading}
              disabled={isEditCardsPending || isLoading}
              onClick={editPaymentCards}
              variant="primary"
              wide
            >
              {t('COST_CENTER.ACCOUNT_DETAILS.SAVE_CHANGES_BUTTON')}
            </Button>
          </HasPermission>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
